import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  firstName: string;
  lastName?: string;
  onboarded: boolean;
  isPremium: boolean;
  referralUserId: string | null;
  onboardingDaysOnTelegram: number;
  xp: number;
}

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  tokenExpiresAt: number | null;
  setUser: (user: User | null) => void;
  setUserOnboarded: () => void;
  setAccessToken: (accessToken: string | null, expiresAt?: number | null) => void;
  clearAuth: () => void;
}

/**
 * Buffer time in milliseconds before token expiration to consider it expired.
 * Using 4 hours.
 */
const TOKEN_EXPIRY_BUFFER_MS = 4 * 60 * 60 * 1000;

/**
 * Check if the stored token is still valid (not expired).
 * Returns true if the token exists and won't expire within the buffer time.
 */
export function isTokenValid(state: Pick<AuthStore, 'accessToken' | 'tokenExpiresAt'>): boolean {
  if (!state.accessToken || !state.tokenExpiresAt) {
    return false;
  }

  const now = Date.now();
  return state.tokenExpiresAt > now + TOKEN_EXPIRY_BUFFER_MS;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      tokenExpiresAt: null,
      setUser: (user: User | null) => {
        set({ user });
      },
      setUserOnboarded: () => {
        set((state) => {
          if (!state.user) return state;
          return { user: { ...state.user, onboarded: true } };
        });
      },
      setAccessToken: (accessToken: string | null, expiresAt?: number | null) => {
        set({ accessToken, tokenExpiresAt: expiresAt ?? null });
      },
      clearAuth: () => {
        set({ user: null, accessToken: null, tokenExpiresAt: null });
      },
    }),
    {
      name: 'auth-store',
    },
  ),
);
